// web/src/copilot/AskTapelensSheet.tsx
//
// Sheet that hosts the CopilotChat UI. Opened by AskTapelensButton.
//
// C6: Mounts <UsageIndicator> in the SheetHeader (under title).
// C8: Mounts <ConversationsList> above the chat; tracks selectedConversationId
//     in local state and passes it to <CopilotKit threadId=...> so the backend
//     can load the matching LangGraph thread. When selectedConversationId is
//     null a fresh conversation starts.
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CopilotKit,
  useCopilotAction,
  useCopilotChat,
  useCopilotChatInternal,
  useCopilotReadable,
} from "@copilotkit/react-core";
import { CopilotChat } from "@copilotkit/react-ui";
import type { RenderMessageProps } from "@copilotkit/react-ui";
import "@copilotkit/react-ui/styles.css";
import { CustomMessage } from "./CustomMessage";
import { useQueryClient } from "@tanstack/react-query";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
// Slice 2 / Task F4: the focused-material readable moved into TradingView
// as part of the richer `dashboard_focus` readable. The sheet no longer
// needs to bridge the per-pane state through FocusedMaterialContext.
import { UsageIndicator } from "./UsageIndicator";
import { ConversationsList } from "./ConversationsList";
import { useSupabaseAuthHeaders } from "./useSupabaseAuthHeaders";
import {
  usePendingContext,
  type PendingContextValue,
} from "./PendingContextProvider";
import { NoteConfirmCard } from "./NoteConfirmCard";
import { useNotesUI } from "@/notes/NotesUIController";
import type { NoteTargetKind } from "@/api/notes";
import { useCreateNote } from "@/notes/hooks";
import {
  CONVERSATIONS_QUERY_KEY,
  USAGE_QUERY_KEY,
  useConversationMessages,
} from "./api";

/**
 * Mounted inside <CopilotKit> so it can subscribe to chat state. Whenever
 * isLoading transitions true → false (a turn just finished), we invalidate
 * the usage and conversations queries so the indicator and dropdown
 * reflect the new state without waiting for the background poll.
 */
function ChatActivityInvalidator() {
  const { isLoading } = useCopilotChat();
  const wasLoading = useRef(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (wasLoading.current === true && isLoading === false) {
      queryClient.invalidateQueries({ queryKey: USAGE_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY });
    }
    wasLoading.current = isLoading;
  }, [isLoading, queryClient]);

  return null;
}

/**
 * One-shot pre-injected context: when a sparkle button was clicked on a
 * cluster/analysis card, that target was written into PendingContext. We
 * register it as an `asked_about` readable so the agent sees the object the
 * user is asking about on the next turn — then immediately clear() so the
 * same target does not bleed into the FOLLOWING turn (intentionally
 * one-shot, per spec §4 Slice 1).
 *
 * Registered every render so hook order is stable; the `available` flag
 * gates whether the value is actually shipped to the agent.
 */
/**
 * Frontend action: `open_notes(target_kind, target_id)`.
 *
 * Registered inside <CopilotKit> so it lives on the same runtime instance
 * the agent reaches. The handler delegates to NotesUIController (lifted to
 * AppShell) so the slide-over the user already has on the dashboard pops
 * open in-place — no separate copy of the notes UI inside the chat.
 *
 * Spec §5.4: "If user not on /dashboard, navigates first." The
 * NotesSlideOver only mounts inside TradingView (the /dashboard route),
 * so calling `openNotes` from /notes, /calendar, /monitors, etc. used to
 * silently no-op even though the controller state flipped. Now we:
 *   1. If pathname !== "/dashboard", navigate("/dashboard").
 *   2. Call openNotes(...) — state lives in the AppShell-level
 *      NotesUIControllerProvider, so it persists across the route
 *      transition. TradingView reads notesOpen + openedTarget on its
 *      initial render and pops the slide-over immediately.
 *
 * Returns `{opened: true}` after the navigate + openNotes calls complete.
 * We don't try to confirm mount synchronously — the navigate is a
 * synchronous router state update + React schedules a render, so by the
 * time the agent's next turn fires the slide-over is up. Returning
 * `{opened: false, reason: "mount_timeout"}` is reserved for a future
 * extension where we observe an actual mount failure (e.g. auth guard
 * redirected away from /dashboard).
 */
function OpenNotesAction() {
  const { openNotes } = useNotesUI();
  const navigate = useNavigate();
  const location = useLocation();
  // Keep a ref so the handler always reads the latest pathname even when
  // the same handler closure is reused across renders by CopilotKit.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useCopilotAction({
    name: "open_notes",
    description:
      "Open the notes thread for a cluster, analysis, or material. " +
      "Use this when the user asks to see notes attached to a specific object.",
    parameters: [
      {
        name: "target_kind",
        type: "string",
        enum: ["cluster", "analysis", "material"],
        required: true,
      },
      { name: "target_id", type: "string", required: true },
    ],
    handler: async ({ target_kind, target_id }) => {
      const target = {
        kind: target_kind as NoteTargetKind,
        id: target_id,
      };
      // If we're not on /dashboard, navigate first. The NotesSlideOver
      // mounts inside TradingView; without this hop the open_notes
      // controller state would flip but nothing would render until the
      // user manually navigated. The state we set after navigate() is
      // picked up by TradingView on its initial render since the
      // NotesUIControllerProvider lives at the AppShell level.
      if (pathnameRef.current !== "/dashboard") {
        navigate("/dashboard");
      }
      openNotes(target);
      return { opened: true };
    },
  });
  return null;
}

/**
 * HIL gate wrapper rendered inside the chat thread by CopilotKit's
 * `renderAndWaitForResponse`. The action's render callback can't host hooks
 * directly (it isn't a React component) — but the JSX it returns CAN, so we
 * lift the timeout effect into this component.
 *
 * Resolution paths:
 *   * Save → mutate + respond({saved, note_id})
 *   * Cancel / ESC → respond({cancelled: true})
 *   * 5-min inactivity → respond({cancelled: true, reason: "timeout"})
 *
 * After any path runs respond() the action interrupt is closed; the
 * component unmounts shortly after as CopilotKit re-renders the message.
 */
function CreateNotePending({
  target,
  draft,
  respond,
}: {
  target: PendingContextValue | null;
  draft: string;
  respond: (result: unknown) => void;
}) {
  const createNote = useCreateNote();
  const respondedRef = useRef(false);

  const respondOnce = useCallback(
    (result: unknown) => {
      if (respondedRef.current) return;
      respondedRef.current = true;
      respond(result);
    },
    [respond],
  );

  // 5-minute inactivity → cancel-with-timeout. Effect runs once per mount;
  // any user interaction that fires respondOnce() before the timeout still
  // wins because respondedRef short-circuits the timer callback.
  useEffect(() => {
    const t = setTimeout(
      () =>
        respondOnce({ cancelled: true, reason: "timeout" }),
      5 * 60 * 1000,
    );
    return () => clearTimeout(t);
  }, [respondOnce]);

  return (
    <NoteConfirmCard
      target={target}
      draft={draft}
      onSave={async (body) => {
        try {
          const note = await createNote.mutateAsync({
            target_kind: target?.kind ?? null,
            target_id: target?.id ?? null,
            body,
          });
          respondOnce({ saved: true, note_id: note.id });
        } catch (err) {
          respondOnce({
            saved: false,
            error: err instanceof Error ? err.message : "unknown_error",
          });
        }
      }}
      onCancel={() => respondOnce({ cancelled: true })}
    />
  );
}

/**
 * Frontend action: `create_note(target_kind?, target_id?, body)`.
 *
 * HIL-gated via CopilotKit's `renderAndWaitForResponse`. The agent drafts a
 * body; the user confirms (Save) or rejects (Cancel/ESC/timeout). The Save
 * path delegates to the existing `useCreateNote` mutation so optimistic
 * updates from F2 still apply.
 */
function CreateNoteAction() {
  useCopilotAction({
    name: "create_note",
    description:
      "Draft a note the user confirms before saving. " +
      "Pass target_kind + target_id for attached notes, or omit both for freeform.",
    parameters: [
      {
        name: "target_kind",
        type: "string",
        enum: ["cluster", "analysis", "material"],
        required: false,
      },
      { name: "target_id", type: "string", required: false },
      { name: "body", type: "string", required: true },
    ],
    renderAndWaitForResponse: ({ status, args, respond }) => {
      if (status !== "executing" || !respond) {
        // While inProgress (streaming args) or after complete, no card.
        return <></>;
      }
      const kind = args.target_kind as NoteTargetKind | undefined;
      const id = args.target_id;
      const body = (args.body as string | undefined) ?? "";
      const target: PendingContextValue | null =
        kind && id ? { kind, id } : null;
      return (
        <CreateNotePending target={target} draft={body} respond={respond} />
      );
    },
  });
  return null;
}

function AskedAboutReadable() {
  const { pending, clear } = usePendingContext();
  const { isLoading } = useCopilotChat();
  // Track whether the agent was in-flight during the last render so we
  // can detect the `true → false` transition that marks "the agent's
  // first turn since pending was set just completed".
  const wasLoadingRef = useRef(false);

  useCopilotReadable({
    description:
      "asked_about: the object the user clicked sparkle on (one-shot pre-injected context)",
    value: pending ?? {},
    available: pending ? "enabled" : "disabled",
  });

  // Clear timing — bind to the chat turn boundary, not setTimeout(0).
  //
  // Previous behavior used `setTimeout(() => clear(), 0)` which flipped
  // the readable's `available` to "disabled" before the user finished
  // typing/submitting their first turn — so the agent NEVER actually
  // received asked_about. The bug shipped silently because the readable
  // was registered with the right value, just immediately invalidated.
  //
  // Option (a): bind to useCopilotChat().isLoading transitioning
  // true → false. CopilotKit flips isLoading=true while the agent is
  // composing a response and back to false on completion. Listening for
  // that edge gives us a precise one-shot signal: pending stays visible
  // through the user's submit + the agent's first turn, then drops
  // before the next turn so it doesn't bleed forward.
  //
  // Option (b) — bind to sheet `open` transitioning false — was the
  // fallback if isLoading wasn't surfaced cleanly. Since CopilotKit
  // exposes useCopilotChat() to any descendant of <CopilotKit>, (a) is
  // both available and more precise; we use it.
  useEffect(() => {
    if (pending && wasLoadingRef.current === true && isLoading === false) {
      clear();
    }
    wasLoadingRef.current = isLoading;
  }, [pending, isLoading, clear]);

  return null;
}

/**
 * Mounted inside <CopilotKit>. On the first mount for a given conversation,
 * fetches the stored message history and injects it into CopilotKit's chat
 * state so the user can see prior turns. Only fires on a fresh empty mount
 * (the `messages.length > 0` guard prevents clobbering live state), and the
 * outer <CopilotKit key={...}> remounts the whole subtree on conversation
 * switch, resetting hydrated.current per conversation automatically.
 *
 * Tool messages are filtered out — they need toolCallId which isn't stored
 * in DB metadata yet. The model still has full context server-side via the
 * conversation history prepend.
 */
function HistoryHydrator({ conversationId }: { conversationId: string | null }) {
  const { data } = useConversationMessages(conversationId);
  const { setMessages, messages } = useCopilotChatInternal();
  const hydrated = useRef(false);

  useEffect(() => {
    if (!data || hydrated.current) return;
    if (messages.length > 0) return; // don't overwrite live state
    const converted = data.messages
      .filter((m) => m.role === "user" || m.role === "assistant")
      .map((m) => ({
        id: String(m.id),
        role: m.role as "user" | "assistant",
        content: m.content ?? "",
      }));
    if (converted.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setMessages(converted as any);
      hydrated.current = true;
    }
  }, [data, messages.length, setMessages]);

  return null;
}

interface AskTapelensSheetProps {
  open: boolean;
  onClose: () => void;
}

export function AskTapelensSheet({ open, onClose }: AskTapelensSheetProps) {
  const { t } = useTranslation("copilot");
  // Slice 2 / Task F4: focused-material publication moved into TradingView
  // as a richer `dashboard_focus` readable. The sheet no longer reads from
  // FocusedMaterialContext — the backend per-turn composer assembles the
  // current view from whichever readables are forwarded by AG-UI.

  // C8: track which conversation the user has selected (null = new/fresh).
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);

  // CopilotKit Runtime base — matches CopilotProvider. The env var is
  // VITE_COPILOTKIT_RUNTIME_URL (NOT VITE_API_BASE_URL) because the
  // CopilotKit Runtime is a separate service from the FastAPI agent
  // backend. The Runtime URL ends in /copilotkit (handled below).
  const runtimeBase =
    import.meta.env.VITE_COPILOTKIT_RUNTIME_URL ?? "http://localhost:8001";

  // The nested <CopilotKit> below overrides the outer provider's settings
  // (incl. headers), so we explicitly forward the Supabase JWT here too.
  // Shared hook handles initial hydration + auto-refresh + 5-min defensive
  // interval so long-lived tabs don't ship expired JWTs.
  const getHeaders = useSupabaseAuthHeaders();

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl flex flex-col p-0"
      >
        <SheetHeader className="px-4 pt-4">
          <SheetTitle>{t("sheet.title")}</SheetTitle>
          {/* C6: usage indicator sits under the title */}
          <UsageIndicator />
        </SheetHeader>

        {/* C8: conversation list above the chat area */}
        <div className="px-4">
          <ConversationsList
            selectedId={selectedConversationId}
            onSelect={setSelectedConversationId}
            onNew={() => setSelectedConversationId(null)}
          />
        </div>

        {/*
          C8 — Resume conversation wiring:
          CopilotKit 1.57.x exposes a `threadId` prop on <CopilotKit>.
          When a conversation is selected we key the inner CopilotKit on that
          ID so it mounts a fresh instance pointed at the existing LangGraph
          thread. Clearing the selection (null) lets a new thread be created.

          Note: CopilotProvider (which wraps the whole app) is the outer
          provider; we need a *nested* CopilotKit here to override threadId
          per-conversation. CopilotKit supports nesting — the innermost
          provider wins for anything it sets.
        */}
        <div className="flex-1 overflow-hidden min-h-0">
          <CopilotKit
            key={selectedConversationId ?? "__new__"}
            runtimeUrl={`${runtimeBase}/copilotkit`}
            headers={getHeaders}
            threadId={selectedConversationId ?? undefined}
          >
            <ChatActivityInvalidator />
            <AskedAboutReadable />
            <OpenNotesAction />
            <CreateNoteAction />
            <HistoryHydrator conversationId={selectedConversationId} />
            <CopilotChat
              labels={{
                // Verified against @copilotkit/react-ui v1.57 CopilotChatLabels
                // shape — see index.d.mts. There is no `thinking` label in
                // 1.57 (the loading state is rendered by the assistant
                // message renderer, not a label). The set we override:
                title: t("sheet.title"),
                initial: t("sheet.initial"),
                placeholder: t("sheet.placeholder"),
                regenerateResponse: t("sheet.regenerate"),
                stopGenerating: t("sheet.stop"),
                error: t("error.transient"),
              }}
              className="h-full"
              RenderMessage={(props: RenderMessageProps) => {
                // The ag-ui Message union type doesn't expose all fields at the
                // TypeScript level on all variants, so we cast through unknown.
                // Note: metadata is intentionally not extracted here — CustomMessage
                // routes on role="reasoning" (set by @ag-ui/client from
                // REASONING_MESSAGE_START events), not on a metadata field.
                const raw = props.message as unknown as {
                  id: string;
                  role?: string;
                  content?: string;
                };
                return (
                  <CustomMessage
                    message={{
                      id: raw.id,
                      role: raw.role ?? "assistant",
                      content: raw.content ?? "",
                    }}
                    streaming={props.inProgress && props.isCurrentMessage}
                    DefaultRenderer={({ message }) => {
                      // Delegate to CopilotKit's built-in role-specific renderers.
                      // Their props interfaces require many internal fields
                      // (isLoading, isGenerating, rawData, ImageRenderer) that
                      // CopilotKit provides at the call site — pass them through
                      // via any to avoid duplicating internal props.
                      const AssistantMsg = props.AssistantMessage;
                      const UserMsg = props.UserMessage;
                      if (message.role === "user" && UserMsg) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        return <UserMsg {...({ message: props.message } as any)} />;
                      }
                      if (AssistantMsg) {
                        // Derive loading/generating state so CopilotKit's
                        // default AssistantMessage can render its LoadingIcon
                        // while waiting for the first text token.
                        const isLoading =
                          props.inProgress &&
                          props.isCurrentMessage &&
                          !raw.content;
                        const isGenerating =
                          props.inProgress &&
                          props.isCurrentMessage &&
                          !!raw.content;
                        return (
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          <AssistantMsg
                            {...({
                              message: props.message,
                              isCurrentMessage: props.isCurrentMessage,
                              isLoading,
                              isGenerating,
                            } as any)}
                          />
                        );
                      }
                      // Fallback: plain text when no built-in renderer is available.
                      // M12: model-generated content is untrusted — isolate
                      // with <bdi> so any latent bidi-override codepoints
                      // cannot reorder surrounding chat UI in RTL context.
                      return <div><bdi>{raw.content ?? ""}</bdi></div>;
                    }}
                  />
                );
              }}
            />
          </CopilotKit>
        </div>
      </SheetContent>
    </Sheet>
  );
}
